import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  ChevronDownIcon,
  MagnifyingGlassIcon,
  UserCircleIcon,
  Bars3Icon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { Menu, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useAuth } from '@/contexts/AuthContext';
import { SimpleThemeToggle } from '@/components/ui/ThemeToggle';
import NotificationDropdown from '@/components/features/NotificationDropdown';
import { logger } from '@/utils/logger';

interface HeaderProps {
  onToggleSidebar?: () => void;
}

export const Header: React.FC<HeaderProps> = ({ onToggleSidebar }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [showMobileSearch, setShowMobileSearch] = useState(false);

  const handleLogout = async () => {
    try {
      await logout();
      logger.userAction('User logged out');
    } catch (error) {
      logger.error('Logout failed', error);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
      logger.userAction('Search performed', { query: searchQuery });
    }
  };

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const toggleMobileSearch = () => {
    setShowMobileSearch(!showMobileSearch);
    if (showMobileSearch) {
      setSearchQuery('');
    }
  };

  return (
    <header className="fixed top-0 right-0 left-0 md:left-64 z-40 h-16 bg-white dark:bg-dark-sidebar border-b border-gray-200 dark:border-dark-border px-4 sm:px-6">
      <div className="flex items-center justify-between h-full">
        {/* Left side */}
        <div className="flex items-center space-x-4">
          {/* Mobile menu button */}
          <button
            onClick={onToggleSidebar}
            className="md:hidden p-2 rounded-lg text-gray-500 hover:text-gray-600 hover:bg-gray-100 dark:text-dark-text-secondary dark:hover:text-dark-text-primary dark:hover:bg-dark-card focus:outline-none focus:ring-2 focus:ring-primary-500 sidebar-toggle"
          >
            <Bars3Icon className="h-6 w-6" />
          </button>

          {/* Search - Desktop */}
          <div className="hidden sm:block flex-1 max-w-lg">
            <form onSubmit={handleSearch} className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={searchQuery}
                onChange={handleSearchInputChange}
                placeholder="Search..."
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-dark-border rounded-lg focus:ring-primary-500 focus:border-primary-500 sm:text-sm bg-white dark:bg-dark-search-bar text-gray-900 dark:text-dark-text-primary placeholder-gray-400 dark:placeholder-dark-text-secondary"
              />
            </form>
          </div>

          {/* Mobile search button */}
          <button 
            onClick={toggleMobileSearch}
            className="sm:hidden p-2 rounded-lg text-gray-500 hover:text-gray-600 hover:bg-gray-100 dark:text-dark-text-secondary dark:hover:text-dark-text-primary dark:hover:bg-dark-card focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            {showMobileSearch ? (
              <XMarkIcon className="h-6 w-6" />
            ) : (
              <MagnifyingGlassIcon className="h-6 w-6" />
            )}
          </button>
        </div>

        {/* Right side */}
        <div className="flex items-center space-x-2 sm:space-x-4">
          {/* Theme Toggle */}
          <SimpleThemeToggle />
          
          {/* Notifications */}
          <NotificationDropdown />

          {/* User menu */}
          <Menu as="div" className="relative">
            <div>
              <Menu.Button className="flex items-center space-x-2 sm:space-x-3 p-1 sm:p-2 text-sm rounded-lg hover:bg-gray-100 dark:hover:bg-dark-card focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2">
                {user?.avatar_url ? (
                  <img
                    src={user.avatar_url}
                    alt={user.name}
                    className="h-7 w-7 sm:h-8 sm:w-8 rounded-full"
                  />
                ) : (
                  <UserCircleIcon className="h-7 w-7 sm:h-8 sm:w-8 text-gray-400" />
                )}
                <div className="hidden lg:block text-left">
                  <div className="text-sm font-medium text-gray-900 dark:text-dark-text-primary">
                    {user?.name}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-dark-text-secondary">
                    {user?.role === 'ADMIN' ? 'Admin' : user?.role === 'SUPER_ADMIN' ? 'Super Admin' : 'User'}
                  </div>
                </div>
                <ChevronDownIcon className="h-4 w-4 text-gray-400 hidden sm:block" />
              </Menu.Button>
            </div>
            
            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Menu.Items className="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white dark:bg-dark-card py-1 shadow-lg ring-1 ring-black dark:ring-dark-border ring-opacity-5 focus:outline-none">
                <Menu.Item>
                  {({ active }) => (
                    <Link
                      to="/profile"
                      className={`${
                        active ? 'bg-gray-100 dark:bg-dark-badge-gray' : ''
                      } block px-4 py-2 text-sm text-gray-700 dark:text-dark-text-primary`}
                    >
                      Profile
                    </Link>
                  )}
                </Menu.Item>
                
                <Menu.Item>
                  {({ active }) => (
                    <Link
                      to="/settings"
                      className={`${
                        active ? 'bg-gray-100 dark:bg-dark-badge-gray' : ''
                      } block px-4 py-2 text-sm text-gray-700 dark:text-dark-text-primary`}
                    >
                      Settings
                    </Link>
                  )}
                </Menu.Item>

                <hr className="my-1 border-gray-200 dark:border-dark-border" />

                <Menu.Item>
                  {({ active }) => (
                    <button
                      onClick={handleLogout}
                      className={`${
                        active ? 'bg-gray-100 dark:bg-dark-badge-gray' : ''
                      } block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-dark-text-primary`}
                    >
                      Logout
                    </button>
                  )}
                </Menu.Item>
              </Menu.Items>
            </Transition>
          </Menu>
        </div>
      </div>

      {/* Mobile Search Overlay */}
      <AnimatePresence>
        {showMobileSearch && (
          <motion.div
            className="sm:hidden fixed top-16 left-0 right-0 z-30 bg-white dark:bg-dark-sidebar border-b border-gray-200 dark:border-dark-border px-4 py-3"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
          >
            <form onSubmit={handleSearch} className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={searchQuery}
                onChange={handleSearchInputChange}
                placeholder="Search..."
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-dark-border rounded-lg focus:ring-primary-500 focus:border-primary-500 text-sm bg-white dark:bg-dark-search-bar text-gray-900 dark:text-dark-text-primary placeholder-gray-400 dark:placeholder-dark-text-secondary"
                autoFocus
              />
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};